import { db } from '../database/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';

const STAFF_COLLECTION = 'staff';
const SALARY_COLLECTION = 'salaries';

// Get all staff members
export const getAllStaff = async () => {
  try {
    const staffRef = collection(db, STAFF_COLLECTION);
    const q = query(staffRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

// Get staff by ID
export const getStaffById = async (staffId) => {
  try {
    const staffRef = doc(db, STAFF_COLLECTION, staffId);
    const staffDoc = await getDoc(staffRef);
    
    if (staffDoc.exists()) {
      return { id: staffDoc.id, ...staffDoc.data() };
    }
    return null;
  } catch (error) {
    console.error('Error fetching staff:', error);
    throw error;
  }
};

// Add new staff member
export const addStaff = async (staffData) => {
  try {
    const staffRef = collection(db, STAFF_COLLECTION);
    const newStaff = {
      ...staffData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: staffData.status || 'active',
    };
    
    const docRef = await addDoc(staffRef, newStaff);
    return { id: docRef.id, ...newStaff };
  } catch (error) {
    console.error('Error adding staff:', error);
    throw error;
  }
};

// Update staff member
export const updateStaff = async (staffId, staffData) => {
  try {
    const staffRef = doc(db, STAFF_COLLECTION, staffId);
    const updateData = {
      ...staffData,
      updatedAt: serverTimestamp(),
    };
    
    await updateDoc(staffRef, updateData);
    return { id: staffId, ...updateData };
  } catch (error) {
    console.error('Error updating staff:', error);
    throw error;
  }
};

// Delete staff member
export const deleteStaff = async (staffId) => {
  try {
    const staffRef = doc(db, STAFF_COLLECTION, staffId);
    await deleteDoc(staffRef);
    return true;
  } catch (error) {
    console.error('Error deleting staff:', error);
    throw error;
  }
};

// Get salary records for a staff member
export const getStaffSalaries = async (staffId) => {
  try {
    const salaryRef = collection(db, SALARY_COLLECTION);
    const q = query(
      salaryRef,
      where('staffId', '==', staffId),
      orderBy('paymentDate', 'desc')
    );
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching salaries:', error);
    throw error;
  }
};

// Add salary payment record
export const addSalaryPayment = async (salaryData) => {
  try {
    const salaryRef = collection(db, SALARY_COLLECTION);
    const newSalary = {
      ...salaryData,
      createdAt: serverTimestamp(),
    };
    
    const docRef = await addDoc(salaryRef, newSalary);
    return { id: docRef.id, ...newSalary };
  } catch (error) {
    console.error('Error adding salary payment:', error);
    throw error;
  }
};

// Get all salary payments
export const getAllSalaryPayments = async () => {
  try {
    const salaryRef = collection(db, SALARY_COLLECTION);
    const q = query(salaryRef, orderBy('paymentDate', 'desc'));
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error('Error fetching salary payments:', error);
    throw error;
  }
};

// Update salary payment
export const updateSalaryPayment = async (salaryId, salaryData) => {
  try {
    const salaryRef = doc(db, SALARY_COLLECTION, salaryId);
    await updateDoc(salaryRef, salaryData);
    return { id: salaryId, ...salaryData };
  } catch (error) {
    console.error('Error updating salary payment:', error);
    throw error;
  }
};

// Delete salary payment
export const deleteSalaryPayment = async (salaryId) => {
  try {
    const salaryRef = doc(db, SALARY_COLLECTION, salaryId);
    await deleteDoc(salaryRef);
    return true;
  } catch (error) {
    console.error('Error deleting salary payment:', error);
    throw error;
  }
};
